import { isNetworkError } from "../api/errors";
import type { Endpoints } from "../api/endpoints";
import type { Category, Product } from "../api/types";
import { db, type CatalogueSnapshot } from "./db";

/** Offline product catalogue (§16.1 open item, in scope): a snapshot of active products and
 * categories per tenant, refreshed while online, read when a catalogue request fails because
 * the network is down. Prices offline are the snapshot's; the server reprices at sync. */

const key = (tenantId: number) => `tenant:${tenantId}`;
let memo: CatalogueSnapshot | null = null;
const listeners = new Set<() => void>();

export async function refreshCatalogue(api: Endpoints, tenantId: number): Promise<CatalogueSnapshot | null> {
  try {
    const products: Product[] = [];
    for (let page = 0; page < 50; page++) {
      const p = await api.products.search("", { page, size: 200 });
      products.push(...p.content);
      if (page >= p.totalPages - 1) break;
    }
    const categories = await api.categories.list();
    const snap: CatalogueSnapshot = { key: key(tenantId), tenantId, products, categories, savedAt: new Date().toISOString() };
    await (await db()).put("catalogue", snap);
    memo = snap;
    listeners.forEach((l) => l());
    return snap;
  } catch {
    return null;
  }
}

export async function getCatalogue(tenantId: number): Promise<CatalogueSnapshot | null> {
  if (memo?.tenantId === tenantId) return memo;
  try {
    memo = (await (await db()).get("catalogue", key(tenantId))) ?? null;
  } catch {
    memo = null;
  }
  return memo;
}

export const subscribeCatalogue = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const catalogueSavedAt = (): string | null => memo?.savedAt ?? null;

/** Run the live request; on a network failure answer from the snapshot instead. */
export async function withOffline<T>(tenantId: number | null, live: () => Promise<T>, local: (s: CatalogueSnapshot) => T | null): Promise<T> {
  try {
    return await live();
  } catch (e) {
    if (!isNetworkError(e) || tenantId === null) throw e;
    const snap = await getCatalogue(tenantId);
    const v = snap ? local(snap) : null;
    if (v === null) throw e;
    return v;
  }
}

const norm = (s: string | null | undefined) => (s ?? "").toLowerCase();
export const local = {
  search: (s: CatalogueSnapshot, q: string): Product[] => {
    const t = q.trim().toLowerCase();
    return s.products.filter((p) => !t || norm(p.name).includes(t) || norm(p.sku).includes(t) || norm(p.barcode).includes(t)).slice(0, 60);
  },
  favorites: (s: CatalogueSnapshot): Product[] => s.products.filter((p) => p.favorite),
  byCategory: (s: CatalogueSnapshot, id: number): Product[] => {
    const ids = new Set([id, ...s.categories.filter((c: Category) => c.parentId === id).map((c) => c.id)]);
    return s.products.filter((p) => p.categoryId !== null && ids.has(p.categoryId));
  },
  barcode: (s: CatalogueSnapshot, code: string): Product | null => s.products.find((p) => p.barcode === code) ?? null,
  byId: (s: CatalogueSnapshot, id: number): Product | null => s.products.find((p) => p.id === id) ?? null,
  categories: (s: CatalogueSnapshot): Category[] => s.categories,
};
